// Core React & Redux
import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {Link} from 'react-router-dom';

// Material UI Core
import Grid from '@mui/material/Unstable_Grid2';
import {
    Button,
    CircularProgress,
    Container,
    FormControl,
    FormControlLabel,
    Paper,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';

// Material UI Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBackIos';
import DoneIcon from '@mui/icons-material/Done';

// Material UI Styles
import {makeStyles} from '@mui/styles';

// Custom Components
import Centered from '../layout/Centered';
import AssessmentProgressBar from '../widgits/AssessmentProgressBar';

// Redux Actions
import {fetchAnswers, sendFlash, submitAnswers, updateAnswer,} from '../../actions';

const IntroversionExtroversion = (props) => {
    const useStyles = makeStyles({
        root: {
            padding: "20px",
        },
        heading: {
            marginBottom: "20px",
            textAlign: "center"
        },
        buttonBox: {
            display: "flex",
            justifyContent: "center",
        },
        button: {
            margin: "10px",
        },
    });

    const classes = useStyles();

    const {assessment} = props;
    const questions = assessment.questions;
    const assessmentId = assessment._id;
    const userId = props.user._id;

    useEffect(() => {
        if (!props.answersLoading) {
            // on load fetch completed answers
            let answerData = {
                userId: props.user._id,
                assessmentId: props.assessment._id,
            };
            props.fetchAnswers(answerData);
        }
    }, []);

    const handleSubmitAnswers = (e) => {
        // e.preventDefault()
        let answerData = {
            userId: userId,
            assessmentId: assessmentId,
            totalQuestions: props.assessment.totalQuestions,
        };
        props.submitAnswers(answerData);
    };

    return (
        <Container className={classes.root}>
            <AssessmentProgressBar assessmentId={assessmentId}/>
            <Centered>
                <Typography variant="h3" className={classes.heading}>
                    {props.assessment.name}
                </Typography>

                {props.answersLoading !== "done" && (
                    <Centered>
                        <CircularProgress/>
                    </Centered>
                )}

                <div>
                    <Typography>
                        <b>What is it?</b>{" "}
                    </Typography>
                    <Typography>
                        This test explores personality fit in the workplace. While many aspects of personality influence
                        career satisfaction, the introversion–extroversion spectrum plays a particularly significant
                        role in determining the types of environments and roles where people thrive.
                    </Typography>
                    <br></br>
                    <Typography>
                        <b>Why is it used?</b>{" "}
                    </Typography>
                    <Typography>
                        It’s used because some jobs, while they may align with your interests or motivations, or other
                        variables, are more comfortable for certain personality types. For example, introverts often
                        thrive in independent, focused roles (like computer-based work), while extroverts may find
                        energy and success in social or client-facing roles (like sales). Even if you’re interested in a
                        job, if it doesn’t fit your natural personality style, it can quickly become draining.
                    </Typography>
                    <br></br>
                    <Typography>
                        <b>Instructions</b>{" "}
                    </Typography>
                    <Typography>
                        Please indicate how much you agree or disagree with each of the following statements (1 =
                        Strongly disagree, 2= disagree, 3 = neutral, 4= agree, 5= strongly agree)
                    </Typography>
                    <br></br>
                    <Typography>
                        <b>Time Estimate:</b> 3 minutes.
                    </Typography>
                    <br></br>
                </div>
                <form>
                    {Object.entries(questions).map(([key, question]) => {
                        return (
                            <Question
                                key={key}
                                number={key}
                                question={question}
                                userId={userId}
                                assessmentId={assessmentId}
                            />
                        );
                    })}
                </form>

                <div className={classes.buttonBox}>
                    <Button
                        variant="contained"
                        className={classes.button}
                        component={Link}
                        to="/"
                        onClick={handleSubmitAnswers}
                        startIcon={<ArrowBackIcon/>}
                    >
                        Save & Exit
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        component={Link}
                        to="/"
                        onClick={handleSubmitAnswers}
                        startIcon={<DoneIcon/>}
                    >
                        I'm Finished
                    </Button>
                </div>
            </Centered>
        </Container>
    );
};

let Question = (props) => {
    const useStyles = makeStyles({
        paper: {
            padding: "20px",
            marginBottom: "20px",
            display: "flex",
        },
        title: {
            display: "inline-block",
            flex: "0 0 20%",
            justifyContent: "center",
            alignItems: "center",
            marginRight: "20px",
            color: "rgb(125, 125, 125)",
        },
        question: {
            display: "block",
        },
    });

    const {userId, assessmentId} = props;
    const classes = useStyles();

    const handleRadioChange = (event) => {
        const questionNum = event.target.name;
        const answer = event.target.value;
        props.updateAnswer(userId, assessmentId, questionNum, answer);
    };

    let answer = props.answer || "";
    return (
        <Paper className={classes.paper}>
            <Grid container spacing={2}>
                <Grid xs={12}>
                    <Typography variant="h5" className={classes.title}>
                        {props.number}. {props.question.question}
                    </Typography>
                </Grid>
                <Grid xs={12}>
                    <FormControl className={classes.question}>
                        <RadioGroup
                            name={props.number}
                            value={answer}
                            onChange={handleRadioChange}
                        >
                            <FormControlLabel
                                value="1"
                                control={<Radio/>}
                                label="Strongly Disagree"
                            />
                            <FormControlLabel
                                value="2"
                                control={<Radio/>}
                                label="Disagree"
                            />
                            <FormControlLabel
                                value="3"
                                control={<Radio/>}
                                label="Unsure"
                            />
                            <FormControlLabel
                                value="4"
                                control={<Radio/>}
                                label="Agree"
                            />
                            <FormControlLabel
                                value="5"
                                control={<Radio/>}
                                label="Strongly Agree"
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            </Grid>
        </Paper>
    );
};

const mapState = (state, ownProps) => {
    const answerIndex = ownProps.number - 1;
    const answers = state.answers.byId["introversion-extroversion"];
    return {
        answer: answers ? answers.answers[answerIndex] : "",
    };
};
Question = connect(mapState, {updateAnswer})(Question);

const mapDispatchToProps = {
    updateAnswer,
    submitAnswers,
    fetchAnswers,
    sendFlash,
};

const mapStateToProps = (state) => {
    const assessmentId = "introversion-extroversion";
    const answers = state.answers.byId[assessmentId];
    return {
        user: state.user,
        assessment: state.assessments.byId[assessmentId],
        answersLoading: answers ? answers.loading : false,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(IntroversionExtroversion);
